import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Download } from "lucide-react";
import Avatar from "../../components/Avatar";
import { StatChip, downloadCSV } from "./_shared";

export default function RosterReport({ home, classification, gender }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const params = {};
    if (home) params.home = home;
    if (classification) params.classification = classification;
    if (gender) params.gender = gender;
    api.get("/staff", { params }).then(({ data }) => setRows(data));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [home, classification, gender]);

  const stats = useMemo(() => ({
    total: rows.length,
    direct: rows.filter(r => r.staff_classification === "Direct Care Worker").length,
    contract: rows.filter(r => r.staff_classification === "Contract Care Worker").length,
    houseMgr: rows.filter(r => r.access_role === "House Manager").length,
    admin: rows.filter(r => r.access_role === "Administration").length,
    male: rows.filter(r => r.gender === "Male").length,
    female: rows.filter(r => r.gender === "Female").length,
  }), [rows]);

  const exportCSV = () => {
    const data = rows.map(r => ({
      full_name: r.full_name, email: r.email || "", home: r.home,
      job_title: r.job_title || r.role_title || "", classification: r.staff_classification || "",
      gender: r.gender || "",
      access_role: r.access_role, active: r.active_status ? "Yes" : "No",
    }));
    downloadCSV(`nv-roster-${new Date().toISOString().slice(0,10)}.csv`, data);
    toast.success("CSV downloaded");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <StatChip label="Total" value={stats.total} tone="sky" />
          <StatChip label="Direct Care Worker" value={stats.direct} tone="sky" />
          <StatChip label="Contract Care Worker" value={stats.contract} tone="purple" />
          <StatChip label="House Manager" value={stats.houseMgr} tone="emerald" />
          <StatChip label="Administration" value={stats.admin} tone="amber" />
          <StatChip label="Male" value={stats.male} tone="slate" />
          <StatChip label="Female" value={stats.female} tone="slate" />
        </div>
        <button onClick={exportCSV} disabled={!rows.length} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold disabled:opacity-50" data-testid="export-roster-csv">
          <Download size={14} /> Export CSV
        </button>
      </div>
      <div className="nv-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="roster-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3 text-left">Name</th>
              <th className="px-3 py-3 text-left">Home</th>
              <th className="px-3 py-3 text-left">Job Title</th>
              <th className="px-3 py-3 text-left">Classification</th>
              <th className="px-3 py-3 text-left">Gender</th>
              <th className="px-3 py-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-5 py-3"><div className="flex items-center gap-2.5"><Avatar name={r.full_name} gender={r.gender} src={r.profile_image} size="sm" /><span className="font-medium text-slate-800">{r.full_name}</span></div></td>
                <td className="px-3 py-3 text-slate-600">{r.home}</td>
                <td className="px-3 py-3 text-slate-600">{r.job_title || r.role_title || "—"}</td>
                <td className="px-3 py-3 text-slate-600">{r.staff_classification || r.access_role || "—"}</td>
                <td className="px-3 py-3 text-slate-600">{r.gender || "—"}</td>
                <td className="px-3 py-3">{r.active_status ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Active</span> : <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Inactive</span>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No staff match the filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
